import React, { useEffect, useRef, useState } from "react";
import { Box, Paper, Typography } from "@mui/material";
import { AIMessage, AIToolCall, AIToolResult } from "src/ai/types";
import { MessageRenderer } from "./MessageRenderer";
import { ToolDetails } from "./ToolDetails";
import { ImageLightbox } from "./ImageLightbox";

interface Props {
  messages: AIMessage[];
  isLoading: boolean;
}

/**
 * Pull every base64 PNG out of execute_python tool results on this message
 * and render them inline below Kate's prose. The model doesn't have to know
 * anything about base64 — it just says "here's a chart" and the chart shows
 * up underneath. Same scheme KateAI uses, minus the localhost file protocol.
 */
const collectPlotImages = (message: AIMessage): string[] => {
  const calls = message.toolCalls ?? [];
  const results: AIToolResult[] = message.toolResults ?? [];
  const resultsById: Record<string, AIToolResult> = {};
  for (const r of results) resultsById[r.toolCallId] = r;

  const images: string[] = [];
  for (const c of calls as AIToolCall[]) {
    if (c.name !== "execute_python") continue;
    const r = resultsById[c.id]?.result as { images?: string[] } | undefined;
    if (Array.isArray(r?.images)) {
      for (const b64 of r.images) {
        if (typeof b64 === "string" && b64.length > 0) {
          images.push(`data:image/png;base64,${b64}`);
        }
      }
    }
  }
  return images;
};

export const MessageList: React.FC<Props> = ({ messages, isLoading }) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [lightboxSrc, setLightboxSrc] = useState<string | null>(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({
      top: scrollRef.current.scrollHeight,
      behavior: "smooth",
    });
  }, [messages.length, isLoading]);

  return (
    <Box
      ref={scrollRef}
      sx={{
        flex: 1,
        overflowY: "auto",
        p: 1.5,
        backgroundColor: "rgba(0,0,0,0.02)",
      }}
    >
      <ImageLightbox src={lightboxSrc} onClose={() => setLightboxSrc(null)} />
      {messages.length === 0 && (
        <Box sx={{ textAlign: "center", color: "text.secondary", mt: 4 }}>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Hi, I'm Kate.
          </Typography>
          <Typography variant="caption">
            Ask me about runs, instruments, or utilization.
            <br />
            <br />
            <br />
            <strong>Examples</strong>
            <br />
            <br />
            How many customer cyto runs failed last week?
            <br />
            <br />
            Generate a bar chart with the number of customer sequencing runs in
            the last 30 days by sequencing kit name?
            <br />
            <br />
            Generate a pareto of failures by error type for cyto runs in the
            internal fleet for the last 30 days
            <br />
            <br />
            Can you perform an error pattern analysis of cyto run failures for
            the last 30 days? Please include both customer and internal runs.
            <br />
            <br />
            Can you perform a fleet review for the last week?
          </Typography>
        </Box>
      )}
      {messages.map((m) => {
        const isUser = m.role === "user";
        const plots = isUser ? [] : collectPlotImages(m);
        return (
          <Box
            key={m.id}
            sx={{
              display: "flex",
              justifyContent: isUser ? "flex-end" : "flex-start",
              mb: 1,
            }}
          >
            <Paper
              elevation={0}
              sx={{
                maxWidth: "85%",
                px: 1.5,
                py: 1,
                backgroundColor: isUser ? "primary.main" : "background.paper",
                color: isUser ? "primary.contrastText" : "text.primary",
                border: isUser ? "none" : "1px solid rgba(0,0,0,0.08)",
                borderRadius: 2,
              }}
            >
              <MessageRenderer content={m.content} />
              {plots.length > 0 && (
                <Box sx={{ mt: 1 }}>
                  {plots.map((src, i) => (
                    <Box
                      key={`${m.id}-plot-${i}`}
                      component="img"
                      src={src}
                      alt={`Chart ${i + 1}`}
                      sx={{
                        maxWidth: "100%",
                        maxHeight: 460,
                        display: "block",
                        my: 0.75,
                        borderRadius: 1,
                        border: "1px solid rgba(0,0,0,0.1)",
                        cursor: "zoom-in",
                        transition: "transform 0.15s ease",
                        "&:hover": { transform: "scale(1.01)" },
                      }}
                      onClick={() => setLightboxSrc(src)}
                    />
                  ))}
                </Box>
              )}
              {!isUser && <ToolDetails message={m} />}
            </Paper>
          </Box>
        );
      })}
    </Box>
  );
};
